import { useCallback, useEffect, useRef } from 'react'

/** Movement below this many pixels is a click, not a drag. */
const DRAG_THRESHOLD_PX = 3

/**
 * Keeps a fixed-position element of size `bounds` fully inside the viewport.
 */
export const clampToViewport = (
  { x, y },
  { width, height },
  viewport = { width: window.innerWidth, height: window.innerHeight }
) => ({
  x: Math.min(Math.max(0, x), Math.max(0, viewport.width - width)),
  y: Math.min(Math.max(0, y), Math.max(0, viewport.height - height))
})

/**
 * CSS placement for the floating element: default bottom-right corner until the user has
 * dragged it once, then absolute left/top.
 */
export const placementStyle = (position, margin, corner = 'bottom-right') => {
  if (position) return { left: position.x, top: position.y }
  return corner === 'bottom-left' ? { left: margin, bottom: margin } : { right: margin, bottom: margin }
}

/**
 * Drag by pointer events (mouse + touch) — spec §6. Spread `handleProps` on the drag handle only:
 * the panel header, or the whole bubble. The body of the panel never drags, so selecting or typing
 * inside it never moves the widget.
 *
 * @param {object} params
 * @param {{x:number,y:number}|null} params.position current position; null = default corner
 * @param {(position: {x:number,y:number}) => void} params.onMove receives the clamped position
 * @param {{width:number,height:number}|(() => {width:number,height:number})} params.bounds size of
 *   the element being dragged, or a getter for it — a getter defers the read (e.g. `offsetHeight`,
 *   0 on first render) to pointerdown time and to each resize re-clamp.
 */
const usePointerDrag = ({ position, onMove, bounds }) => {
  const dragged = useRef(false)
  // Read through a ref so the resize effect does not re-subscribe on every render when `bounds`
  // is an inline getter (a fresh function identity each time).
  const boundsRef = useRef(bounds)
  boundsRef.current = bounds
  const resolveBounds = useCallback(() => {
    const current = boundsRef.current
    return typeof current === 'function' ? current() : current
  }, [])

  // A remembered position must stay valid when the window shrinks.
  useEffect(() => {
    if (!position) return

    const reclamp = () => {
      const clamped = clampToViewport(position, resolveBounds())
      if (clamped.x !== position.x || clamped.y !== position.y) onMove(clamped)
    }
    window.addEventListener('resize', reclamp)
    return () => window.removeEventListener('resize', reclamp)
  }, [position, resolveBounds, onMove])

  const onPointerDown = useCallback((event) => {
    // Primary button only (mouse); touch/pen report button 0 or -1.
    if (event.button > 0) return

    const handle = event.currentTarget
    const rect = handle.getBoundingClientRect()
    // Without a position the element sits in its CSS default corner: the handle is its top-left.
    const origin = position ?? { x: rect.left, y: rect.top }
    const start = { x: event.clientX, y: event.clientY }
    const size = resolveBounds()
    dragged.current = false
    if (handle.setPointerCapture) handle.setPointerCapture(event.pointerId)

    const move = (moveEvent) => {
      const dx = moveEvent.clientX - start.x
      const dy = moveEvent.clientY - start.y
      if (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX) dragged.current = true
      onMove(clampToViewport({ x: origin.x + dx, y: origin.y + dy }, size))
    }
    const stop = () => {
      handle.removeEventListener('pointermove', move)
      handle.removeEventListener('pointerup', stop)
      handle.removeEventListener('pointercancel', cancel)
      if (handle.releasePointerCapture) handle.releasePointerCapture(event.pointerId)
    }
    // A cancelled gesture is never followed by a click, so there is nothing to swallow.
    const cancel = () => {
      dragged.current = false
      stop()
    }
    handle.addEventListener('pointermove', move)
    handle.addEventListener('pointerup', stop)
    handle.addEventListener('pointercancel', cancel)
  }, [position, onMove, resolveBounds])

  // Browsers fire `click` after pointerdown+pointerup on the same element even when the pointer
  // travelled; a drag of the bubble must not also open it.
  const onClickCapture = useCallback((event) => {
    if (!dragged.current) return
    dragged.current = false
    event.stopPropagation()
    event.preventDefault()
  }, [])

  return {
    handleProps: {
      onPointerDown,
      onClickCapture,
      style: { cursor: 'move', touchAction: 'none' }
    }
  }
}

export default usePointerDrag
